/* eslint-disable @typescript-eslint/naming-convention */
import { FeatureFlag } from '@ps/web-core-enums';
import { useFeature } from '@ps/web-core-store';
import type { SiteHeader } from '@ps/web-martech-cms-types';
import type {
  MainNavigationBlock,
  SecondaryNavigationBlock
} from '@ps/web-martech-cms-types/SiteHeader';
import { buttonClick as trackButtonClick } from '@ps/web-shared-analytics';
import { Link } from '@ps/web-shared-components';
import classNames from 'clsx';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';

import { Vary } from '@swf/components/ResponseHeaders/ResponseHeaders';
import { Header } from '@swf/core/enums';
import { useRefs } from '@swf/core/providers/RefsProvider';
import { select } from '@swf/core/store';

import { filterMainNavItems, filterSecondaryNavItems } from '../../utils/utils';

import styles from './CMSHeaderV2.scss';
import AppDownloadBanner from './components/AppDownloadBanner/AppDownloadBanner';
import BrandIconV2 from './components/BrandIcon/BrandIconV2';
import BrandLinks from './components/BrandLinks/BrandLinks';
import LoginMenu from './components/LoginMenu/LoginMenu';
import RamAccount from './components/RamAccount/RamAccount';
import SidebarV2 from './components/Sidebar/SidebarV2';

const CMSHeaderV2: React.FC<SiteHeader> = ({
  brand_icon,
  brand_icon_url,
  layout = 'Real Money',
  main_navigation,
  secondary_navigation,
  support_navigation
}) => {
  const headerRef = useRef<HTMLDivElement>(null);
  const topHeaderRef = useRef<HTMLDivElement>(null);
  const { removeRef, setRefs } = useRefs();
  const [menuOpen, setMenuOpen] = useState(false);
  const toggleMenu = useCallback(() => {
    setMenuOpen((prevMenuOpen) => !prevMenuOpen);
    !menuOpen && trackButtonClick({ buttonName: 'Hamburger Menu' });
  }, [menuOpen]);
  const useRam1 = useFeature(FeatureFlag.UseRam1);
  const casinoNonAuth = useFeature(FeatureFlag.CasinoNonAuth);
  const sportsNonAuth = useFeature(FeatureFlag.SportsNonAuth);
  const isEmbedded = useSelector(select.device.isEmbedded);
  const disableRam = useFeature(FeatureFlag.DisableRam);
  const isMobile = useSelector(select.device.isMobile);
  const embeddedRoute = useSelector(select.location.isEmbeddedRoute);
  const showWebContent = !isEmbedded && !embeddedRoute;
  const page = useSelector(select.cms.getPage);
  const clickableLogoUrl = page?.clickable_logo_url || brand_icon_url;
  const { pathname } = useLocation();

  const filteredMainNavigation: MainNavigationBlock[] = filterMainNavItems(main_navigation, {
    casinoNonAuth,
    sportsNonAuth
  });

  const filteredSecondaryNavigation: SecondaryNavigationBlock[] = filterSecondaryNavItems(
    secondary_navigation,
    pathname
  );

  useEffect(() => {
    setRefs('header', headerRef);
    setRefs('topHeader', topHeaderRef);

    return () => {
      removeRef('header');
      removeRef('topHeader');
    };
  }, [setRefs, removeRef]);

  useEffect(() => {
    if (menuOpen) {
      (document.documentElement as HTMLElement).scrollTop = 0;
    }
  }, [menuOpen]);

  return layout === 'Hidden' ? null : (
    <>
      {showWebContent && <AppDownloadBanner />}
      <header
        id="CMSheader"
        ref={headerRef}
        className={classNames(styles.header, {
          [styles.desktopHeader]: !isMobile
        })}
      >
        <Vary by={[Header.Auth]} />
        <div className={classNames(styles.menu)}>
          <div className={styles.brandLinksWrapper}>
            <span className={classNames(styles.menuButton)} onClick={toggleMenu}>
              <Link href={clickableLogoUrl} track trackText="Brand Icon">
                <BrandIconV2 brandIcon={brand_icon} />
              </Link>
              <div className={styles.burgerMenu} data-testid="burger-menu">
                <span className={styles.burgerSlice} />
                <span className={styles.burgerSlice} />
                <span className={styles.burgerSlice} />
              </div>
            </span>
            <BrandLinks
              main={filteredMainNavigation}
              classes={{
                root: styles.brandLinks,
                navItem: styles.brandLinkItem,
                active: styles.brandLinkItemActive
              }}
            />
            {layout === 'Real Money' && (
              <div className={styles.loginMenuWrapper}>
                {!disableRam && (
                  <div className={styles.accountLinks}>
                    {useRam1 ? (
                      <RamAccount />
                    ) : (
                      <LoginMenu
                        classes={{
                          loggedIn: {
                            account: styles.loggedInAccount,
                            deposit: styles.loggedInDeposit
                          }
                        }}
                      />
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
          {isMobile && (
            <SidebarV2
              menuOpen={menuOpen}
              toggleMenu={toggleMenu}
              brandIcon={brand_icon}
              brandIconUrl={clickableLogoUrl}
              mainNavigation={filteredMainNavigation}
              secondaryNavigation={filteredSecondaryNavigation}
              supportNavigation={support_navigation}
            />
          )}
        </div>
      </header>
    </>
  );
};

export default CMSHeaderV2;
